import React from 'react';
import { srcSetFor } from '../responsiveImage';
import usePageMetadata, { truncate, SITE_NAME } from '../usePageMetadata';

function EventPage({ event, onBack, isAuthenticated, onEdit, onDelete, onImageClick }) {
  // Each event is its own indexable URL, so it needs its own title, description
  // and link preview rather than inheriting the home page's.
  const heading = event.pageTitle || event.title;
  usePageMetadata({
    title: `${heading}${event.date ? ` — ${event.date}` : ''} | ${SITE_NAME}`,
    description: truncate(event.pageDescription || event.description, 155),
    image: event.image || undefined,
    path: `/events/${event.id}`,
    event,
  });

  return (
    <div>
      <header className="bg-wine shadow-lg">
        <nav className="max-w-6xl mx-auto px-4 py-5 flex justify-between items-center">
          <button onClick={onBack} className="text-white text-2xl font-bold tracking-wider flex items-center gap-2 hover:text-gold transition">
            ← Back to Home
          </button>
          {isAuthenticated && (
            <div className="flex gap-3">
              <button onClick={() => onEdit(event)} aria-label={`Edit ${event.title}`} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold transition">
                ✏️ Edit
              </button>
              <button onClick={() => onDelete(event.id)} aria-label={`Delete ${event.title}`} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold transition">
                🗑️ Delete
              </button>
            </div>
          )}
        </nav>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <div className="text-gold-dark font-bold text-xl mb-2">{event.date}</div>
          <h1 className="text-5xl md:text-6xl font-bold text-wine mb-6">{event.pageTitle || event.title}</h1>
          <div className="text-xl text-gray-600 max-w-4xl mx-auto whitespace-pre-line leading-relaxed">
            {event.pageDescription || event.description}
          </div>
        </div>

        {event.pageImages && event.pageImages.length > 0 && (
          <div className="mt-16">
            <h2 className="text-4xl font-bold text-center text-wine mb-12">Gallery</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {event.pageImages.map((image, index) => (
                <button
                  type="button"
                  key={index}
                  className="rounded-xl overflow-hidden shadow-lg hover-lift border-0 p-0 bg-transparent"
                  style={{ cursor: 'pointer' }}
                  aria-label={`View larger photo ${index + 1} from ${event.title}`}
                  onClick={() => onImageClick && onImageClick(image, `${event.title} - Photo ${index + 1}`)}
                >
                  <img
                    src={image}
                    srcSet={srcSetFor(image)}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    alt={`${event.title} photo ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="600"
                    className="w-full h-64 object-contain"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="bg-gray-900 text-white text-center py-8 mt-20">
        <p className="mb-4">&copy; 2025 Vivat Alfa Winery. All rights reserved.</p>
        <button onClick={onBack} className="text-gold hover:text-white transition font-bold">
          ← Back to Home
        </button>
      </footer>
    </div>
  );
}

export default EventPage;